import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { CustomEncoder } from '@app/common/custom-http-param-encode';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { Group } from '@microsoft/microsoft-graph-types-beta';
import { endOfDay } from 'date-fns';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, map, shareReplay, tap, toArray } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GroupsService {

  private readonly cacheKeyAllGroups = 'GroupsService-getAllGroups';
  private readonly cacheKeyAllOwnedGroups = 'GroupsService-getAllMyOwnedGroups';
  private readonly cacheKeyMemberGroups = 'GroupsService-getAllMyMemberGroups';
  private readonly cacheKeyGroupsWithFilter = 'GroupsService-getAllO365Groups';
  private readonly cacheKeyAllMyTransitiveMemberGroups = 'Group-Service-getAllMyTransitiveMemberGroups';
  public useCache = true;
  private getAllOwnedGroups$: Observable<Array<Group>>;
  private getAllMemberGroups$: Observable<Array<Group>>;
  private getAllGroups$: Observable<Array<Group>>;

  constructor(private http: HttpClient, private cache: CacheService, private httpHelper: HttpHelper) { }

  getAllGroups(doNotIncludeSecurityGroups?: boolean): Observable<Array<Group>> {
    if (this.getAllGroups$) {
      return this.getAllGroups$;
    } else {
      let httpParams = new HttpParams({ encoder: new CustomEncoder() })
        .append('$select', `id,displayname,description,resourceProvisioningOptions,creationOptions,visibility,mailNickname,groupTypes`)
        .append('$top', `500`);

      if (doNotIncludeSecurityGroups) {
        httpParams = httpParams
          .append('$filter', `groupTypes/any(c:c+eq+'Unified')`);
      }

      this.getAllGroups$ = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_BETA_VERSION}/groups`, { params: httpParams })
        .pipe(
          expand((results) => {
            if (results.hasOwnProperty('@odata.nextLink')) {
              return this.http.get<any>(results['@odata.nextLink']);
            } else {
              return EMPTY;
            }
          }),
          toArray(),
          map(results => {
            console.log(`fetched All groups result: `);
            let allGroups = new Array<Group>();
            results.forEach(result => {
              allGroups = allGroups.concat(result.value as Array<Group>);
            });
            return allGroups;
          }),
          tap(response => {
            this.useCache && this.cache.set(this.cacheKeyAllGroups, response as Array<Group>);
          }),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(this.cacheKeyAllGroups);
        if (cachedData) {
          return concat(of(cachedData), this.getAllGroups$);
        }
      }
      return this.getAllGroups$;
    }
  }

  getAllMyOwnedGroups(doNotIncludeSecurityGroups?: boolean): Observable<Array<Group>> {
    if (this.getAllOwnedGroups$) {
      return this.getAllOwnedGroups$;
    } else {
      const httpParams = new HttpParams({ encoder: new CustomEncoder() })
        .append('$select', `id,displayname,description,resourceProvisioningOptions,creationOptions,visibility,groupTypes,mailNickname`)
        .append('$top', `500`);

      this.getAllOwnedGroups$ = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/me/ownedObjects/$/microsoft.graph.group`, { params: httpParams })
        .pipe(
          expand((results) => {
            if (results.hasOwnProperty('@odata.nextLink')) {
              return this.http.get<any>(results['@odata.nextLink']);
            } else {
              return EMPTY;
            }
          }),
          toArray(),
          map(results => {
            console.log(`fetched owned groups result: ` + results);
            let ownedGroups = new Array<Group>();
            results.forEach(result => {
              const groups = doNotIncludeSecurityGroups ? (result.value as Array<Group>).filter(g => g.groupTypes.indexOf('Unified') > -1) : (result.value as Array<Group>);
              ownedGroups = ownedGroups.concat(groups);
            });
            return ownedGroups;
          }),
          tap(response => {
            this.useCache && this.cache.set(this.cacheKeyAllOwnedGroups, response);
          }),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(this.cacheKeyAllOwnedGroups);
        if (cachedData) {
          return concat(of(cachedData), this.getAllOwnedGroups$);
        }
      }
      return this.getAllOwnedGroups$;
    }
  }

  getAllMyMemberGroups(doNotIncludeSecurityGroups?: boolean): Observable<Array<Group>> {
    if (this.getAllMemberGroups$) {
      return this.getAllMemberGroups$;
    } else {
      const httpParams = new HttpParams({ encoder: new CustomEncoder() })
        .append('$select', `id,displayname,description,resourceProvisioningOptions,creationOptions,visibility,groupTypes,mailNickname`)
        .append('$top', `500`);

      this.getAllMemberGroups$ = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT}${Constants.GRAPH_API_VERSION_ONE}/me/memberOf/$/microsoft.graph.group`, { params: httpParams })
        .pipe(
          expand((results) => {
            if (results.hasOwnProperty('@odata.nextLink')) {
              return this.http.get<any>(results['@odata.nextLink']);
            } else {
              return EMPTY;
            }
          }),
          toArray(),
          map(results => {
            console.log(`fetched member groups result: `, results);
            let memberGroups = new Array<Group>();
            results.forEach(result => {
              const groups = doNotIncludeSecurityGroups ? (result.value as Array<Group>).filter(g => g.groupTypes.indexOf('Unified') > -1) : (result.value as Array<Group>);
              memberGroups = memberGroups.concat(groups);
            });
            return memberGroups;
          }),
          tap(response => {
            this.useCache && this.cache.set(this.cacheKeyMemberGroups, response, endOfDay(new Date()));
          }),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Group>>(this.cacheKeyMemberGroups);
        if (cachedData) {
          return concat(of(cachedData), this.getAllMemberGroups$);
        }
      }
      return this.getAllMemberGroups$;
    }
  }

  getGroupsWithFilter(top: number, filter: string): Observable<Array<Group>> {
    // HACK: the normal angular encoder does not work with this graph api.

    filter = filter.replace(/&/, '%26').replace(/'/, '\'\''); // escape & and '
    const httpParams = new HttpParams({ encoder: new CustomEncoder() })
      .append('$filter', `groupTypes/any(c:c eq 'Unified') and startswith(displayName, '${filter}')`)
      .append('$top', top.toString());

    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/groups`, { params: httpParams })
      .pipe(map(result => {
        console.log(`fetched groups result: `, result.value);
        console.log(result.value);
        return result.value as Array<Group>;
      }),
        tap(data => {
          this.useCache && this.cache.set(this.cacheKeyGroupsWithFilter, data);
        }),
        catchError(this.httpHelper.handleError({ operation: 'getGroupsWithFilter', result: new Array<Group>() })));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<Group>>(this.cacheKeyGroupsWithFilter);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  // Return all the groups that the user is a member of. The check is transitive, unlike reading the memberOf navigation property, which returns only the groups that the user is a direct member of.
  getAllMyTransitiveMemeberGroups(): Observable<Array<string>> {
    const fetchOperation = this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/getMemberGroups`, { securityEnabledOnly: false })
    .pipe(map(result => {
      console.log(`fetched transitive group result: `, (result.value).length);
      return result.value as Array<string>;
    }),
      tap(data => {
        this.useCache && this.cache.set(this.cacheKeyAllMyTransitiveMemberGroups, data);
      }),
      catchError(this.httpHelper.handleError({ operation: 'getAllMyTransitiveMemeberGroups', result: new Array<string>() })));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<string>>(this.cacheKeyAllMyTransitiveMemberGroups);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }
}
